import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CONTROL_STYLE, INVOICE_STATUSES, PAYMENT_STATUSES } from '../../constants/invoiceConstants';

/**
 * Component for displaying and managing invoice details (number, date, status)
 * 
 * @param {Object} props - Component props
 * @param {string} props.invoiceNumber - Invoice number
 * @param {Date} props.date - Invoice date
 * @param {Date} props.dueDate - Invoice due date
 * @param {string} props.status - Invoice status
 * @param {string} props.paymentStatus - Payment status (optional, hidden by default)
 * @param {boolean} props.readOnlyInvoiceNumber - Make invoice number read-only (default: false)
 * @param {boolean} props.readOnlyStatus - Make status read-only (default: false)
 * @param {boolean} props.showPaymentStatus - Show payment status field (default: false)
 * @param {Function} props.onInvoiceNumberChange - Function to call when invoice number changes
 * @param {Function} props.onDateChange - Function to call when date changes
 * @param {Function} props.onDueDateChange - Function to call when due date changes
 * @param {Function} props.onStatusChange - Function to call when status changes
 * @param {Function} props.onPaymentStatusChange - Function to call when payment status changes
 */
const InvoiceDetailsSection = ({
  invoiceNumber,
  date,
  dueDate,
  status,
  paymentStatus,
  readOnlyInvoiceNumber = false,
  readOnlyStatus = false,
  showPaymentStatus = false,
  quotationReference = null,
  onInvoiceNumberChange,
  onDateChange,
  onDueDateChange,
  onStatusChange,
  onPaymentStatusChange
}) => {
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        Invoice Details
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item xs={12} sm={quotationReference ? 4 : 6}>
          <TextField
            label="Invoice Number"
            value={invoiceNumber}
            onChange={(e) => onInvoiceNumberChange(e.target.value)}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{
              readOnly: readOnlyInvoiceNumber,
            }}
            sx={CONTROL_STYLE}
          />
        </Grid>
        {quotationReference && (
          <Grid item xs={12} sm={4}>
            <TextField
              label="Quotation Reference"
              value={quotationReference}
              fullWidth
              margin="dense"
              size="small"
              InputProps={{
                readOnly: true,
              }}
              sx={{
                ...CONTROL_STYLE,
                '& .MuiInputBase-input': {
                  color: 'primary.main',
                  fontWeight: 500
                }
              }}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={quotationReference ? 4 : 6}>
          {readOnlyStatus ? (
            <TextField
              label="Status"
              value={status}
              fullWidth
              margin="dense"
              size="small"
              InputProps={{
                readOnly: true,
              }}
              sx={CONTROL_STYLE}
            />
          ) : (
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => onStatusChange(e.target.value)}
                label="Status"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: 36,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 0,
                  },
                  '& .MuiSelect-select': {
                    py: 0.75,
                    fontSize: '0.875rem',
                    borderRadius: 0,
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                <MenuItem value={INVOICE_STATUSES.DRAFT} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#4361ee', fontWeight: 500 }}>Draft</Box>
                </MenuItem>
                <MenuItem value={INVOICE_STATUSES.PENDING} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#ff9800', fontWeight: 500 }}>Pending</Box>
                </MenuItem>
                <MenuItem value={INVOICE_STATUSES.COMPLETED} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#2ecd6f', fontWeight: 500 }}>Completed</Box>
                </MenuItem>
                <MenuItem value={INVOICE_STATUSES.CANCELLED} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#ff5252', fontWeight: 500 }}>Cancelled</Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Invoice Date"
              value={date}
              onChange={onDateChange}
              format="dd/MM/yyyy"
              maxDate={new Date()}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: CONTROL_STYLE
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Due Date"
              value={dueDate}
              onChange={onDueDateChange}
              format="dd/MM/yyyy"
              maxDate={new Date()}
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: CONTROL_STYLE
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        {showPaymentStatus && (
          <Grid item xs={12}>
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Payment Status</InputLabel>
              <Select
                value={paymentStatus}
                onChange={(e) => onPaymentStatusChange(e.target.value)}
                label="Payment Status"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    height: 36,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 0,
                  },
                  '& .MuiSelect-select': {
                    py: 0.75,
                    fontSize: '0.875rem',
                    borderRadius: 0,
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              >
                <MenuItem value={PAYMENT_STATUSES.PARTIALLY_PAID} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#4361ee', fontWeight: 500 }}>Partially Paid</Box>
                </MenuItem>
                <MenuItem value={PAYMENT_STATUSES.PENDING} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#ff5252', fontWeight: 500 }}>Pending</Box>
                </MenuItem>
                <MenuItem value={PAYMENT_STATUSES.PAID} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#2ecd6f', fontWeight: 500 }}>Paid</Box>
                </MenuItem>
                <MenuItem value={PAYMENT_STATUSES.NOT_PAID} sx={{ fontSize: '0.875rem' }}>
                  <Box sx={{ color: '#ff9800', fontWeight: 500 }}>Not Paid</Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default InvoiceDetailsSection;
